/* eslint-disable */
import React, { useEffect, useRef, useState } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import toast from 'react-hot-toast';

// Hooks
import useTwilioVideo from '../../hooks/useTwilioVideo';
import { useMutationSaveMeetingTranscript, useMutationSaveAIPermission } from '../../hooks/react-query/meetings';
import { getUserDetails } from '../../utils';
import { ROLES } from '../../constants';

// Sub-components
import {
  JoinMeetingScreen,
  LoadingOverlay,
  MainVideoDisplay,
  PipVideoBox,
  VideoControls,
  AIPermissionDialog,
  RecordingIndicator,
  LiveCaptions,
} from './components';

/**
 * Twilio Video Call Component (Refactored)
 * Main orchestrator for video call functionality
 */
function VideoCall({
  meetingDetails,
  userName,
  email,
  surveyDataNotFilled,
  isSurveySubmitted,
  inviteId,
  setShowSurveyModal,
  onManualEndCall,
}) {
  console.log("meetingDetails", meetingDetails);
  // Context and navigation
  const outletContext = useOutletContext();
  const socketInstance = outletContext?.[8];
  const navigate = useNavigate();

  // Local state
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isCallStarted, setIsCallStarted] = useState(false);
  const [isCaptionsEnabled, setIsCaptionsEnabled] = useState(false);
  const [showAIPermissionDialog, setShowAIPermissionDialog] = useState(false);
  const [aiPermission, setAiPermission] = useState(null); // null = not asked, true = allowed, false = denied
  const [aiPermissionAskedBy, setAiPermissionAskedBy] = useState(null);
  const containerRef = useRef(null);
  const videoCallHookRef = useRef({ getTranscript: null });
  const hasShownPermissionDialog = useRef(false);

  // User and meeting details
  const currentUser = getUserDetails();
  const meetingId = meetingDetails?._id;

  const recipientId =
    meetingDetails?.inviteId?.from?._id === currentUser?.id
      ? meetingDetails?.inviteId?.recipient?._id
      : meetingDetails?.inviteId?.from?._id;

  const remoteParticipant =
    meetingDetails?.inviteId?.from?._id === currentUser?.id
      ? meetingDetails?.inviteId?.recipient
      : meetingDetails?.inviteId?.from;

  // Participant details
  const currentUserImage = currentUser?.image || '/assets/images/avatars/avatar_default.jpg';
  const currentUserName = `${currentUser?.fname} ${currentUser?.lname}`;
  const remoteParticipantImage = remoteParticipant?.image || '/assets/images/avatars/avatar_default.jpg';
  const remoteParticipantName = `${remoteParticipant?.fname || ''} ${remoteParticipant?.lname || ''}`.trim();

  // Load AI permission from meetingDetails when component mounts
  useEffect(() => {
    if (meetingDetails?.aiNotetakerPermission !== undefined && meetingDetails?.aiNotetakerPermission !== null) {
      console.log('✅ Loading existing AI permission from meetingDetails:', meetingDetails.aiNotetakerPermission);
      setAiPermission(meetingDetails.aiNotetakerPermission);
      
      // Set who asked for permission
      if (meetingDetails?.aiNotetakerAskedBy) {
        setAiPermissionAskedBy(meetingDetails.aiNotetakerAskedBy._id);
      }
    } else {
      console.log('ℹ️ No AI permission set yet (permission is null)');
    }
  }, [meetingDetails]);

  // React Query mutations
  const { mutate: saveTranscript } = useMutationSaveMeetingTranscript(
    currentUser?.token,
    toast,
    navigate
  );

  const { mutate: saveAIPermission } = useMutationSaveAIPermission(
    currentUser?.token,
    toast,
    navigate
  );

  // Twilio Video hook
  const {
    localStream,
    isCallActive,
    isAudioEnabled,
    isVideoEnabled,
    isRemoteVideoEnabled,
    isRemoteAudioEnabled,
    hasRemoteParticipant,
    error,
    isReconnecting,
    isConnecting,
    isScreenSharing,
    isRemoteScreenSharing,
    isScreenSharingLoading,
    isRemoteScreenSharingLoading,
    tokenLoading,
    tokenError,
    reconnectAttempts,
    statusMessage,
    statusMessageType,
    transcript,
    startCall,
    endCall,
    reconnect,
    toggleAudio,
    toggleVideo,
    startScreenShare,
    stopScreenShare,
    getTranscript,
    localVideoRef,
    remoteVideoRef,
    debugScreenShare,
  } = useTwilioVideo(socketInstance, meetingId, recipientId, () => {
    // Callback invoked when call ends
    console.log('=== AI NOTETAKER DEBUG START ===');

    // Get transcript from hook
    let meetingTranscript = videoCallHookRef.current.getTranscript();

    // If transcript is empty, try to load from localStorage as fallback
    if (!meetingTranscript || meetingTranscript.trim().length === 0) {
      try {
        const storageKey = `meeting_transcript_${meetingId}`;
        const savedData = localStorage.getItem(storageKey);
        if (savedData) {
          const parsed = JSON.parse(savedData);
          if (parsed.transcript && parsed.transcript.length > 0) {
            meetingTranscript = parsed.transcript;
          }
        }
      } catch (error) {
        console.error('Error loading transcript from localStorage:', error);
      }
    }



    // Always save transcript to database if available (even if empty, backend will handle it)
    if (meetingTranscript && meetingTranscript.trim().length > 0) {
      saveTranscript({ meetingId, transcript: meetingTranscript });

      // Clear localStorage after successful save (will be cleared after backend confirms)
      // We'll keep it for now in case of network errors
    } else {
      console.warn('⚠️ No transcript available to send.');
    }

    console.log('=== AI NOTETAKER DEBUG END ===');

    // Handle navigation/survey
    const shouldShowSurvey = surveyDataNotFilled || !isSurveySubmitted;
    if (shouldShowSurvey) {
      setShowSurveyModal(true);
    } else {
      window.location.href = `${window.location.origin}/dashboard/upcoming-events`;
    }
  });

  // Store getTranscript in ref
  videoCallHookRef.current.getTranscript = getTranscript;

  /**
   * Handle AI permission - Allow
   */
  const handleAIPermissionAllow = () => {
    console.log('AI permission allowed');
    setShowAIPermissionDialog(false);
    setAiPermission(true);
    setAiPermissionAskedBy(currentUser?.id);

    // Save to database
    saveAIPermission({ meetingId, permission: true });

    // Broadcast to other participants via socket
    if (socketInstance) {
      socketInstance.emit('setAIPermission', {
        meetingId,
        permission: true,
      });
    }

    toast.success('AI Note-Taker enabled. Meeting will be transcribed and summarized.', {
      duration: 4000,
    });
  };

  /**
   * Handle AI permission - Deny
   */
  const handleAIPermissionDeny = () => {
    console.log('AI permission denied');
    setShowAIPermissionDialog(false);
    setAiPermission(false);
    setAiPermissionAskedBy(currentUser?.id);

    // Save to database
    saveAIPermission({ meetingId, permission: false });

    // Broadcast to other participants via socket
    if (socketInstance) {
      socketInstance.emit('setAIPermission', {
        meetingId,
        permission: false,
      });
    }

    toast('AI Note-Taker disabled. Only raw transcript will be recorded.', {
      duration: 4000,
      icon: 'ℹ️',
    });
  };

  /**
   * Handle join meeting
   */
  const handleJoinMeeting = () => {
    console.log('Starting Twilio Video call...');

    if (!socketInstance || !meetingId) {
      console.error('Socket or meeting ID missing');
      return;
    }

    socketInstance.emit(
      'joinVideoRoom',
      { meetingId, recipientId },
      (response) => {
        if (response.success) {
          console.log('Joined video room successfully');
          startCall();
          setIsCallStarted(true);

          // Check if AI permission needs to be asked — only Edifiers can allow/deny; Teachers see no dialog
          setTimeout(() => {
            const isEdifier = currentUser?.role === ROLES.EDIFIER;
            if (isEdifier && !hasShownPermissionDialog.current && aiPermission === null) {
              setShowAIPermissionDialog(true);
              hasShownPermissionDialog.current = true;
            }
            
          }, 2000);
        } else {
          console.error('Failed to join video room:', response.error);
        }
      }
    );
  };

  /**
   * Leave meeting
   */
  const leaveMeeting = () => {
    endCall();
    setIsCallStarted(false);
    if (typeof onManualEndCall === 'function') {
      onManualEndCall();
    }
  };

  /**
   * Toggle fullscreen
   */
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  /**
   * Toggle live captions
   */
  const toggleCaptions = () => {
    setIsCaptionsEnabled(!isCaptionsEnabled);
    toast(isCaptionsEnabled ? 'Captions hidden' : 'Live captions enabled', {
      duration: 2000,
      icon: isCaptionsEnabled ? '🔇' : '📝',
    });
  };

  /**
   * Handle screen share toggle
   */
  const handleToggleScreenShare = () => {
    if (isScreenSharing) {
      stopScreenShare();
    } else {
      startScreenShare();
    }
  };

  // Socket listeners for AI permission updates
  useEffect(() => {
    if (!socketInstance) return;

    const handleAIPermissionUpdated = (data) => {
      console.log('AI permission updated via socket:', data);
      if (data.meetingId === meetingId) {
        setAiPermission(data.permission);
        setAiPermissionAskedBy(data.setBy);
        
        // Close the dialog if it's open (for second participant who might have seen it briefly)
        if (showAIPermissionDialog) {
          setShowAIPermissionDialog(false);
        }
        
        // If this is the second participant, show them a notification
        if (data.setBy !== currentUser?.id) {
          if (data.permission) {
            toast.success('AI Note-Taker has been enabled for this meeting', {
              duration: 4000,
            });
          } else {
            toast('AI Note-Taker has been disabled for this meeting', {
              duration: 4000,
              icon: 'ℹ️',
            });
          }
        }
      }
    };

    socketInstance.on('aiPermissionUpdated', handleAIPermissionUpdated);

    return () => {
      socketInstance.off('aiPermissionUpdated', handleAIPermissionUpdated);
    };
  }, [socketInstance, meetingId, currentUser?.id, showAIPermissionDialog]);

  // Fullscreen change listener
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (isCallActive) {
        endCall();
      }
    };
  }, []);

  // Debug function exposure
  useEffect(() => {
    if (debugScreenShare) {
      window.debugScreenShare = debugScreenShare;
    }
    return () => {
      delete window.debugScreenShare;
    };
  }, [debugScreenShare]);

  return (
    <Box sx={{ width: '100%', height: '100vh', position: 'relative', overflow: 'hidden' }}>
      {!isCallStarted ? (
        /* Pre-call screen */
        <JoinMeetingScreen
          participantName={remoteParticipantName}
          onJoinMeeting={handleJoinMeeting}
          isLoading={tokenLoading}
          hasError={!!tokenError}
        />
      ) : (
        /* Video Call Interface */
        <Box
          ref={containerRef}
          sx={{
            width: '100%',
            height: '100vh',
            backgroundColor: '#000',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Main Video Container */}
          <Box
            sx={{
              width: '100%',
              height: '100vh',
              position: 'relative',
              backgroundColor: '#1a1a1a',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {/* Loading Overlays */}
            {isConnecting && (
              <LoadingOverlay type="connecting" />
            )}

            {isReconnecting && !isConnecting && (
              <LoadingOverlay type="reconnecting" errorMessage={error} />
            )}

            {isScreenSharingLoading && (
              <LoadingOverlay type="screen-sharing" />
            )}

            {isRemoteScreenSharingLoading && (
              <LoadingOverlay type="remote-screen-sharing" />
            )}

            {/* Main Video Display */}
            <MainVideoDisplay
              remoteVideoRef={remoteVideoRef}
              localVideoRef={localVideoRef}
              hasRemoteParticipant={hasRemoteParticipant}
              isRemoteVideoEnabled={isRemoteVideoEnabled}
              isRemoteScreenSharing={isRemoteScreenSharing}
              isVideoEnabled={isVideoEnabled}
              isRemoteAudioEnabled={isRemoteAudioEnabled}
              isAudioEnabled={isAudioEnabled}
              remoteParticipantImage={remoteParticipantImage}
              remoteParticipantName={remoteParticipantName}
              currentUserImage={currentUserImage}
              currentUserName={currentUserName}
              statusMessage={statusMessage}
              statusMessageType={statusMessageType}
            />

            {/* Picture-in-Picture Video */}
            {localStream && hasRemoteParticipant && (
              <PipVideoBox
                localVideoRef={localVideoRef}
                localStream={localStream}
                isVideoEnabled={isVideoEnabled}
                isScreenSharing={isScreenSharing}
                isAudioEnabled={isAudioEnabled}
                currentUserImage={currentUserImage}
                currentUserName={currentUserName}
              />
            )}
          </Box>

          {/* AI Permission Dialog */}
          <AIPermissionDialog
            open={showAIPermissionDialog}
            onAllow={handleAIPermissionAllow}
            onDeny={handleAIPermissionDeny}
          />

          {/* Recording Indicator */}
          <RecordingIndicator aiPermission={aiPermission} />

          {/* Live Captions */}
          <LiveCaptions transcript={transcript} isVisible={isCaptionsEnabled} />

          {/* Video Controls */}
          <VideoControls
            isCallActive={isCallActive}
            isAudioEnabled={isAudioEnabled}
            isVideoEnabled={isVideoEnabled}
            isScreenSharing={isScreenSharing}
            isRemoteScreenSharing={isRemoteScreenSharing}
            isFullscreen={isFullscreen}
            isCaptionsEnabled={isCaptionsEnabled}
            onToggleAudio={toggleAudio}
            onToggleVideo={toggleVideo}
            onToggleScreenShare={handleToggleScreenShare}
            onEndCall={leaveMeeting}
            onToggleFullscreen={toggleFullscreen}
            onToggleCaptions={toggleCaptions}
          />
        </Box>
      )}
    </Box>
  );
}

export default VideoCall;
